import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { cloneDeep } from 'lodash';

import FetchModule from '../../../Model/Share/Network/FetchModule';
import { useLanguage } from '../../../Context/LanguageContext';
import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';

import { ReactComponent as KakaoLogoSvg } from '../../../image/KakaoLogo.svg';
import SettingInput from "./SettingInput";
import IndexSettingInput from "./IndexSettingInput";

// 카카오 알림톡 비즈 템플릿 고정 문구 (승인된 템플릿이라 프론트 하드코딩 표시) //
const NOTICE_TEXT = '[Linko] 새로운 체육관 이용 가이드 안내\n\n안녕하세요 {user_name} 회원님\n{store_name} 센터 이용을 위한 안내사항이 새롭게 게시되었습니다.\n자세한 내용은 앱에서 확인해주세요.\n본 안내는 현재 서비스를 이용 중인 회원님께 발송되었습니다.\n\n링코와 함께 더 건강한 일상을 만들어요';
const EXPIRY_TEXT = '[Linko] 회원권 만료예정 안내\n안녕하세요 {user_name}님\n{store_name}에서의 {membership_name} 회원권 종료일이 {days} 일 남았습니다.\n운동의 흐름이 끊기지 않도록\n자세한 내용은 링코 앱에서 확인해주세요.';

export default function KakaoNotificationBox() {
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  // 카카오 알림톡 비즈 템플릿 설정 (문구·일수는 조회 전용, 토글·시간만 편집) //
  const [kakaoSettings, setKakaoSettings] = useState({
    notice: { templateCode: 'NOTIFICATION', isEnabled: false, applyDay: 0, applyTime: '00:00' },
    fiveDay: { templateCode: 'REMAIN_MEMBERSHIP', isEnabled: false, applyDay: 5, applyTime: '00:00' },
    threeDay: { templateCode: 'REMAIN_MEMBERSHIP', isEnabled: false, applyDay: 3, applyTime: '00:00' },
    oneDay: { templateCode: 'REMAIN_MEMBERSHIP', isEnabled: false, applyDay: 1, applyTime: '00:00' }
  });
  const [willSaved, setWillSaved] = useState(null);
  const [rendering, setRendering] = useState(false);

  const FiveDayTimerRef = useRef(null);
  const ThreeDayTimerRef = useRef(null);
  const OneDayTimerRef = useRef(null);

  // 카카오 알림톡 설정 조회 //
  useEffect(() => {
    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.getDataVersion2('kakao_setting/', 'GET');

        if (responseData.status === 200) {
          let newSettings = cloneDeep(kakaoSettings);

          // 공지는 templateCode로, 만료 알림톡은 REMAIN_MEMBERSHIP 공통이라 applyDay(5/3/1)로 구분 //
          responseData.data.forEach((data) => {
            if (data.templateCode === 'NOTIFICATION') assignSetting(newSettings.notice, data);
            else if (data.templateCode === 'REMAIN_MEMBERSHIP') {
              if (data.applyDay === 5) assignSetting(newSettings.fiveDay, data);
              else if (data.applyDay === 3) assignSetting(newSettings.threeDay, data);
              else if (data.applyDay === 1) assignSetting(newSettings.oneDay, data);
            }
          });

          setKakaoSettings(newSettings);
        }
      }
    )();
  }, [rendering])

  // 서버 응답(시/분 정수) → applyTime "HH:MM" 문자열로 정규화 (화면 표시용) //
  function assignSetting(target, data) {
    let hour = String(data.applyHour ?? 0).padStart(2, '0');
    let minute = String(data.applyMinute ?? 0).padStart(2, '0');

    target.templateCode = data.templateCode;
    target.isEnabled = data.isEnabled;
    target.applyDay = data.applyDay;
    target.applyTime = `${hour}:${minute}`;
  }

  // applyTime "HH:MM" 문자열 → { hour, minute } 정수 (백엔드 전송용) //
  function splitTime(value) {
    if (typeof value === 'string' && value.includes(':')) {
      const [hour, minute] = value.split(':');

      return { hour: parseInt(hour, 10) || 0, minute: parseInt(minute, 10) || 0 };
    }

    return { hour: parseInt(value, 10) || 0, minute: 0 };
  }

  // 설정 1건 → kakao/save 요청 형식 (text는 비즈 템플릿 고정이라 미전송) //
  function buildRequest(setting) {
    let time = splitTime(setting.applyTime);

    return {
      templateCode: setting.templateCode,
      isEnabled: setting.isEnabled,
      applyDay: setting.applyDay,
      applyHour: time.hour,
      applyMinute: time.minute
    };
  }

  // 변수 → 칩 렌더링 //
  function renderText(value, extraMap = {}) {
    const variableMap = {
      '{store_name}': t('messageSend.variable.storeName'),
      '{user_name}': t('messageSend.variable.userName'),
      '{membership_name}': t('messageSend.variable.membershipName'),
      ...extraMap,
    };
    return value.split('\n').map((line, lineIdx) => (
      <MessageLine key={lineIdx}>
        {line === '' ? ' ' : line.split(/(\{[^}]+\})/g).map((part, i) => {
          const label = variableMap[part];
          if (label) return <VariableChip key={i}>{label.replace(/^\{|\}$/g, '')}</VariableChip>;
          return part ? <span key={i}>{part}</span> : null;
        })}
      </MessageLine>
    ));
  }

  // 카카오 설정 4건 일괄 저장 //
  function saveKakaoSettings(settings) {
    let requestData = {
      kakaoSettings: [
        buildRequest(settings.notice),
        buildRequest(settings.fiveDay),
        buildRequest(settings.threeDay),
        buildRequest(settings.oneDay)
      ]
    };

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('kakao_setting/save', 'POST', requestData);

        // 저장 실패시 서버 값으로 재동기화 //
        if (responseData.status !== 200) setRendering(prev => !prev);
      }
    )();
  }

  // 사용여부 토글 클릭시 (즉시 저장) //
  function onClickToggle(key) {
    let newSettings = cloneDeep(kakaoSettings);

    newSettings[key].isEnabled = !newSettings[key].isEnabled;

    setKakaoSettings(newSettings);
    saveKakaoSettings(newSettings);
  }

  // 만료 알림톡 발송시간 변경시 (3초 디바운스 저장) //
  function changeExpiryTime(key, timerRef, value) {
    if (value === '') return;

    let newSettings = cloneDeep(kakaoSettings);

    newSettings[key].applyTime = value;

    setKakaoSettings(newSettings);
    setWillSaved(newSettings);

    if (timerRef.current !== null) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      saveKakaoSettings(newSettings);
      setWillSaved(null);
    }, 3000);
  }

  // Input 포커싱 잃는경우 바로 저장 //
  function onBlurTime() {
    if (willSaved !== null) {
      saveKakaoSettings(willSaved);

      if (FiveDayTimerRef.current !== null) clearTimeout(FiveDayTimerRef.current);
      if (ThreeDayTimerRef.current !== null) clearTimeout(ThreeDayTimerRef.current);
      if (OneDayTimerRef.current !== null) clearTimeout(OneDayTimerRef.current);

      setWillSaved(null);
    }
  }

  // 만료 알림톡 5/3/1 행 렌더링 (토글·시간 편집, 일수·문구 조회 전용) //
  function renderExpiryRow(key, timerRef) {
    const setting = kakaoSettings[key];

    return (
      <SettingColumnBoxWrapper>
        <SettingColumnBox>
          <SlideComponentWrapper>
            <SlideOutBox isClicked={setting.isEnabled} onClick={() => onClickToggle(key)}>
              <SlideInBox isClicked={setting.isEnabled} />
            </SlideOutBox>
            <CheckBoxTextWrapper>
              <Text fontFamily='NotoSansKR-Regular' fontSize={16} color={theme.text}>{t('messageSend.editModal.kakaoExpiryDesc')}</Text>
              <SettingCaptionTextWrapper>
                <InputWrapper>
                  <SettingInput
                    value={setting.applyDay}
                    setValue={() => {}}
                    placeholder={0}
                    isReadOnly={true} />
                </InputWrapper>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText}>{t('messageSend.autoMessage.daysBefore')}</Text>
                <InputWrapper onClick={e => e.stopPropagation()}>
                  <IndexSettingInput
                    index={0}
                    value={setting.applyTime}
                    setValue={(value) => changeExpiryTime(key, timerRef, value)}
                    onBlur={onBlurTime}
                    placeholder='00:00'
                    isTimeType={true} />
                </InputWrapper>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText}>{t('messageSend.autoMessage.atTime')}</Text>
              </SettingCaptionTextWrapper>
            </CheckBoxTextWrapper>
          </SlideComponentWrapper>
          <SettingTextWrapper>
            <TextWrapper>
              <MessageText>{renderText(EXPIRY_TEXT, { '{days}': String(setting.applyDay) })}</MessageText>
            </TextWrapper>
          </SettingTextWrapper>
        </SettingColumnBox>
      </SettingColumnBoxWrapper>
    );
  }

  return (
    <TopLevelWrapper>
      <DivisionBox>
        <TopTextWrapper>
          <KakaoLogoIcon />
          <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.primary}>{t('messageSend.editModal.titleKakaoNotification')}</Text>
        </TopTextWrapper>
        <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subText}>{t('messageSend.editModal.descKakaoNotification')}</Text>
      </DivisionBox>
      <SettingBox>
        <SettingColumnBoxWrapper>
          <SettingColumnBox>
            <SlideComponentWrapper onClick={() => onClickToggle('notice')}>
              <SlideOutBox isClicked={kakaoSettings.notice.isEnabled}>
                <SlideInBox isClicked={kakaoSettings.notice.isEnabled} />
              </SlideOutBox>
              <CheckBoxTextWrapper>
                <Text fontFamily='NotoSansKR-Regular' fontSize={16} color={theme.text}>{t('messageSend.editModal.kakaoNoticeDesc')}</Text>
                <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.subText}>{t('messageSend.editModal.kakaoNoticeSentImmediately')}</Text>
              </CheckBoxTextWrapper>
            </SlideComponentWrapper>
            <SettingTextWrapper>
              <TextWrapper>
                <MessageText>{renderText(NOTICE_TEXT)}</MessageText>
              </TextWrapper>
            </SettingTextWrapper>
          </SettingColumnBox>
        </SettingColumnBoxWrapper>

        <DivisionLine />

        {renderExpiryRow('fiveDay', FiveDayTimerRef)}

        <DivisionLine />

        {renderExpiryRow('threeDay', ThreeDayTimerRef)}

        <DivisionLine />

        {renderExpiryRow('oneDay', OneDayTimerRef)}
      </SettingBox>
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 10px;
`;

const DivisionBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  height: 53px;

  gap: 6px;
`;

const TopTextWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 10px;
`;

const SettingBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 10px 20px;

  box-sizing: border-box;
  width: 100%;

  gap: 10px;

  border-radius: 10px;
  background-color: ${props => props.theme.subBackground};
`;

const SettingColumnBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
`;

const SettingColumnBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
  min-height: 40px;
`;

const SlideComponentWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  padding-right: 20px;

  box-sizing: border-box;
  width: 370px;
  height: 100%;

  gap: 30px;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const SlideOutBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  flex-shrink: 0;
  box-sizing: border-box;
  width: 70px;
  height: 32px;

  border: ${props => props.isClicked ? `1px solid ${props.theme.primary}` : `1px solid ${props.theme.line}`};
  border-radius: 9px;
  background-color: ${props => props.isClicked ? props.theme.primary : props.theme.subBackground};

  transition: all 0.3s ease-in-out;
`;

const SlideInBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  margin-left: ${props => props.isClicked ? '43px' : '4px'};

  width: 22px;
  height: 22px;

  border-radius: 7px;
  background-color: ${props => props.isClicked ? props.theme.white : props.theme.line};

  transition: all 0.3s ease-in-out;
`;

const CheckBoxTextWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  gap: 5px;
`;

const SettingCaptionTextWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 5px;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 50px;
  height: 30px;
`;

const SettingTextWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 566px;
  height: 100%;

  border-left: 1px solid ${props => props.theme.line};
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  padding: 10px 15px;

  box-sizing: border-box;
  width: 100%;
`;

const MessageText = styled.div`
  display: block;
`;

const MessageLine = styled.div`
  display: block;

  font-family: NotoSansKR-Regular;
  font-size: 14px;
  line-height: 21px;

  color: ${props => props.theme.text};

  &:not(:last-child) {
    margin-bottom: 5px;
  }
`;

const VariableChip = styled.span`
  display: inline-flex;

  align-items: center;

  padding: 0 5px;

  height: 21px;

  border-radius: 5px;
  background-color: ${props => props.theme.primaryAlpha20};

  font-family: NotoSansKR-Regular;
  font-size: 14px;
  color: ${props => props.theme.text};

  transition: all 0.3s ease-in-out;
`;

const DivisionLine = styled.div`
  margin: 10px 0px;

  width: calc(100% - 20px);
  height: 1px;

  background-color: ${props => props.theme.line};
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;
  color: ${props => props.color};

  margin: 0 0 0 0;

  @media (hover: hover) {
    &:hover {
      cursor: ${props => props.hover};
    }
  }

  transition: all 0.3s ease-in-out;
`;

const KakaoLogoIcon = styled(KakaoLogoSvg)`
  width: 24px;
  height: 24px;

  flex-shrink: 0;

  border-radius: 3px;
`;
